import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, Outlet, useLocation, useParams } from 'react-router';

import { orderApi, orderKeys } from '@/entities/order';
import { reportApi, reportKeys, type OrderReport } from '@/entities/report';
import { useSessionStore } from '@/shared/api';
import { TabHeaderTargetsProvider } from '@/shared/ui';

import { patterns } from '../router/paths';
import { OrderHeader } from './OrderHeader';
import {
    buildOrderTabs,
    needsReleaseCheck,
    orderTabAccessFor,
    type OrderTabKind,
} from './order-tabs';

/** De schermen binnen deze kop, met het tabblad dat bij elk hoort. */
const ROUTE_TABS: ReadonlyArray<readonly [string, OrderTabKind]> = [
    [patterns.dossier, 'overview'],
    [patterns.report, 'report'],
    [patterns.clientPage, 'clientPage'],
];

/**
 * De schil om de schermen van één keuring: Overzicht, Rapport en Klantpagina.
 *
 * Eén keer in de router gehangen, rond `/keuringen/:orderId/...`: de kop met de
 * weg terug, wat deze keuring is en de tabbladen, en daaronder het scherm van
 * het open tabblad. Dat scherm zet zijn stand en knoppen met `TabHeader` in
 * deze kop.
 *
 * Opname en energielabel hangen er bewust niet in. Dat zijn werkplekken op de
 * tablet, offline en over de volle hoogte, met een eigen compacte balk en een
 * weg terug; ze werken bovendien op de uuid van de opname, niet van de keuring.
 * De tabbladen hier openen ze wel.
 *
 * De keuring komt van `GET /orders/{order}` - dezelfde query als het overzicht,
 * dus daar geen extra verzoek. Of de koper al een versie heeft, zegt
 * `GET /orders/{order}/report`, dezelfde query als Rapport en Klantpagina; die
 * gaat alleen uit als er een rapport kan zijn.
 */
export function OrderLayout() {
    const { orderId = '' } = useParams<{ orderId: string }>();
    const { pathname } = useLocation();
    const session = useSessionStore((state) => state.session);
    const access = orderTabAccessFor(session);

    const detail = useQuery({
        queryKey: orderKeys.detail(orderId),
        queryFn: () => orderApi.show(orderId),
        enabled: orderId !== '',
    });
    const order = detail.data ?? null;

    const orderReport = useQuery({
        queryKey: reportKeys.forOrder(orderId),
        queryFn: () => reportApi.forOrder(orderId),
        enabled: needsReleaseCheck(order, access),
    });
    useOrderFollowsReport(orderId, orderReport.data);

    const tabs = buildOrderTabs({
        orderUuid: orderId,
        order,
        access,
        released: orderReport.data === undefined ? null : Boolean(orderReport.data?.released),
        current: ROUTE_TABS.find(([pattern]) => matchPath(pattern, pathname))?.[1] ?? null,
    });

    /* De plekken in de kop bestaan pas na de eerste render; daarna vult het scherm ze. */
    const [aside, setAside] = useState<HTMLElement | null>(null);
    const [notes, setNotes] = useState<HTMLElement | null>(null);
    const targets = useMemo(() => ({ aside, notes }), [aside, notes]);

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
            <OrderHeader order={order} tabs={tabs} asideRef={setAside} notesRef={setNotes} />

            {/* Het scherm krijgt de hoogte ónder de kop. Een scherm dat `min-h-full` rekent
                (de klantpagina) rekent zo vanaf hier, anders viel zijn onderkant weg. */}
            <div className="flex min-h-0 flex-1 flex-col">
                <TabHeaderTargetsProvider targets={targets}>
                    <Outlet />
                </TabHeaderTargetsProvider>
            </div>
        </div>
    );
}

/**
 * Goedkeuren of versturen op het tabblad Rapport rondt de keuring af. De kop
 * toont haar status, dus verandert de versie van de koper of het moment van
 * versturen, dan haalt de kop de keuring opnieuw op. Anders stond er na
 * "Goedkeuren en versturen" nog minutenlang "Concept rapport".
 */
function useOrderFollowsReport(orderUuid: string, orderReport: OrderReport | null | undefined) {
    const queryClient = useQueryClient();
    const stand =
        orderReport === undefined
            ? undefined
            : `${orderReport?.released?.reportUuid ?? ''}|${orderReport?.report.sentAt ?? ''}`;
    const previous = useRef(stand);

    useEffect(() => {
        if (previous.current !== undefined && stand !== undefined && previous.current !== stand) {
            void queryClient.invalidateQueries({ queryKey: orderKeys.detail(orderUuid) });
        }

        previous.current = stand;
    }, [orderUuid, queryClient, stand]);
}
